import { useState, useRef, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import DashboardLayout from '../components/layout/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Separator } from '../components/ui/separator';
import { AlertCircle, Calculator, ArrowRight, Upload, FileText, Sparkles, X, CheckCircle, Coins, Gift, Briefcase, Info } from 'lucide-react';
import { toast } from 'sonner';
import SimonaAssistant from '../components/SimonaAssistant';

// Detect native iOS/Android Capacitor app
const isNativeApp = !!(window.Capacitor && window.Capacitor.isNativePlatform());

// Lista CCNL disponibili
const CCNL_LIST = [
  { id: 'commercio_confcommercio', nome: 'Commercio - Confcommercio' },
  { id: 'metalmeccanici_industria', nome: 'Metalmeccanici - Federmeccanica' },
  { id: 'edilizia_industria', nome: 'Edilizia - Industria' },
  { id: 'turismo_pubblici_esercizi', nome: 'Turismo - Pubblici Esercizi' },
  { id: 'logistica_trasporti', nome: 'Logistica e Trasporti' },
  { id: 'chimico_farmaceutico', nome: 'Chimico Farmaceutico' },
];

import { API_URL } from '../config';

const MONTHS = [
  { value: 1, label: 'Gennaio' },
  { value: 2, label: 'Febbraio' },
  { value: 3, label: 'Marzo' },
  { value: 4, label: 'Aprile' },
  { value: 5, label: 'Maggio' },
  { value: 6, label: 'Giugno' },
  { value: 7, label: 'Luglio' },
  { value: 8, label: 'Agosto' },
  { value: 9, label: 'Settembre' },
  { value: 10, label: 'Ottobre' },
  { value: 11, label: 'Novembre' },
  { value: 12, label: 'Dicembre' },
];

const currentYear = new Date().getFullYear();
const YEARS = Array.from({ length: 10 }, (_, i) => currentYear - i);

export default function UploadPage() {
  const navigate = useNavigate();
  const fileInputRef = useRef(null);
  const [loading, setLoading] = useState(false);
  const [extracting, setExtracting] = useState(false);
  const [error, setError] = useState('');
  const [selectedFile, setSelectedFile] = useState(null);
  const [extractionConfidence, setExtractionConfidence] = useState(null);
  const [creditInfo, setCreditInfo] = useState(null);
  
  // Stato per CCNL e Livello manuale
  const [selectedCcnl, setSelectedCcnl] = useState('');
  const [selectedLivello, setSelectedLivello] = useState('');
  const [livelliDisponibili, setLivelliDisponibili] = useState([]);
  const [loadingLivelli, setLoadingLivelli] = useState(false);
  const [showCcnlSelector, setShowCcnlSelector] = useState(false);
  
  // Stato per Simona (assistente virtuale)
  const [simonaStage, setSimonaStage] = useState('welcome');
  const [simonaMinimized, setSimonaMinimized] = useState(false);
  
  useEffect(() => {
    checkCredits();
  }, []);

  // Carica i livelli quando cambia il CCNL selezionato
  useEffect(() => {
    if (selectedCcnl) {
      fetchLivelli(selectedCcnl);
    } else {
      setLivelliDisponibili([]);
      setSelectedLivello('');
    }
  }, [selectedCcnl]);

  const fetchLivelli = async (ccnlId) => {
    setLoadingLivelli(true);
    try {
      const response = await axios.get(`${API_URL}/ccnl/${ccnlId}/livelli`);
      setLivelliDisponibili(response.data.livelli || []);
    } catch (err) {
      console.error('Error fetching livelli:', err);
      setLivelliDisponibili([]);
    } finally {
      setLoadingLivelli(false);
    }
  };

  const checkCredits = async () => {
    try {
      const response = await axios.get(`${API_URL}/credits/can-check`);
      setCreditInfo(response.data);
    } catch (err) {
      console.error('Error checking credits:', err);
    }
  };
  
  const [formData, setFormData] = useState({
    // Anagrafica
    employee_name: '',
    fiscal_code: '',
    company_name: '',
    period_month: '',
    period_year: '',
    
    // Retribuzione
    gross_salary: '',
    base_salary: '',
    contingency: '0',
    third_element: '0',
    overtime_hours: '0',
    overtime_amount: '0',
    bonuses: '0',
    
    // Trattenute
    inps_employee: '',
    irpef: '',
    regional_tax: '0',
    municipal_tax: '0',
    other_deductions: '0',
    
    // Netto e TFR
    net_salary: '',
    tfr_accrued: '0'
  });

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSelectChange = (name, value) => {
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleFileSelect = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      if (!file.name.toLowerCase().endsWith('.pdf')) {
        toast.error('Seleziona un file PDF');
        return;
      }
      if (file.size > 10 * 1024 * 1024) {
        toast.error('File troppo grande (max 10MB)');
        return;
      }
      setSelectedFile(file);
      setSimonaStage('pdf_uploaded'); // Aggiorna Simona
    }
  };

  const handleExtractPDF = async () => {
    if (!selectedFile) return;
    
    setExtracting(true);
    setError('');
    setExtractionConfidence(null);
    
    try {
      const formDataUpload = new FormData();
      formDataUpload.append('file', selectedFile);
      
      const response = await axios.post(`${API_URL}/extract-pdf`, formDataUpload, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });
      
      const extracted = response.data;
      
      // Update form with extracted data
      setFormData({
        employee_name: extracted.employee_name || '',
        fiscal_code: extracted.fiscal_code || '',
        company_name: extracted.company_name || '',
        period_month: extracted.period_month ? String(extracted.period_month) : '',
        period_year: extracted.period_year ? String(extracted.period_year) : '',
        gross_salary: extracted.gross_salary ? String(extracted.gross_salary) : '',
        base_salary: extracted.base_salary ? String(extracted.base_salary) : '',
        contingency: String(extracted.contingency || 0),
        third_element: String(extracted.third_element || 0),
        overtime_hours: String(extracted.overtime_hours || 0),
        overtime_amount: String(extracted.overtime_amount || 0),
        bonuses: String(extracted.bonuses || 0),
        inps_employee: extracted.inps_employee ? String(extracted.inps_employee) : '',
        irpef: extracted.irpef ? String(extracted.irpef) : '',
        regional_tax: String(extracted.regional_tax || 0),
        municipal_tax: String(extracted.municipal_tax || 0),
        other_deductions: String(extracted.other_deductions || 0),
        net_salary: extracted.net_salary ? String(extracted.net_salary) : '',
        tfr_accrued: String(extracted.tfr_accrued || 0)
      });
      
      setExtractionConfidence(extracted.confidence || 'medium');
      toast.success('Dati estratti con successo! Verifica e conferma i valori.');
      setSimonaStage('extraction_done'); // Aggiorna Simona
      
    } catch (err) {
      const message = err.response?.data?.detail || 'Errore nell\'estrazione del PDF';
      setError(message);
      toast.error(message);
    } finally {
      setExtracting(false);
    }
  };

  const clearFile = () => {
    setSelectedFile(null);
    setExtractionConfidence(null);
    setSimonaStage('welcome'); // Reset Simona
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    
    // Check credits before submitting
    if (!creditInfo?.can_check) {
      toast.error('Crediti insufficienti. Acquista crediti per continuare.');
      navigate('/credits');
      return;
    }
    
    setLoading(true);

    try {
      // Convert string values to numbers
      const payload = {
        employee_name: formData.employee_name,
        fiscal_code: formData.fiscal_code.toUpperCase(),
        company_name: formData.company_name,
        period_month: parseInt(formData.period_month),
        period_year: parseInt(formData.period_year),
        gross_salary: parseFloat(formData.gross_salary) || 0,
        base_salary: parseFloat(formData.base_salary) || 0,
        contingency: parseFloat(formData.contingency) || 0,
        third_element: parseFloat(formData.third_element) || 0,
        overtime_hours: parseFloat(formData.overtime_hours) || 0,
        overtime_amount: parseFloat(formData.overtime_amount) || 0,
        bonuses: parseFloat(formData.bonuses) || 0,
        inps_employee: parseFloat(formData.inps_employee) || 0,
        irpef: parseFloat(formData.irpef) || 0,
        regional_tax: parseFloat(formData.regional_tax) || 0,
        municipal_tax: parseFloat(formData.municipal_tax) || 0,
        other_deductions: parseFloat(formData.other_deductions) || 0,
        net_salary: parseFloat(formData.net_salary) || 0,
        tfr_accrued: parseFloat(formData.tfr_accrued) || 0,
        // CCNL e Livello selezionati manualmente
        ccnl_manuale: selectedCcnl || null,
        livello_manuale: selectedLivello || null
      };

      const response = await axios.post(`${API_URL}/payslips`, payload);
      
      toast.success('Busta paga caricata con successo!');
      navigate(`/payslip/${response.data.id}`);
    } catch (err) {
      const message = err.response?.data?.detail || 'Errore nel caricamento';
      setError(message);
      toast.error(message);
      
      // If payment required, redirect to credits
      if (err.response?.status === 402) {
        navigate('/credits');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <DashboardLayout>
      <div className="max-w-4xl mx-auto space-y-6" data-testid="upload-page">
        {/* Credit Status Banner */}
        {creditInfo && (
          <Card className={`swiss-card ${creditInfo.can_check ? 'border-emerald-200 bg-emerald-50/50' : 'border-amber-200 bg-amber-50/50'}`}>
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  {creditInfo.is_free ? (
                    <>
                      <Gift className="w-5 h-5 text-emerald-600" />
                      <div>
                        <p className="font-medium text-emerald-800">Primo controllo gratuito!</p>
                        <p className="text-sm text-emerald-600">Questo controllo non consumerà crediti</p>
                      </div>
                    </>
                  ) : creditInfo.can_check ? (
                    <>
                      <Coins className="w-5 h-5 text-emerald-600" />
                      <div>
                        <p className="font-medium text-emerald-800">Hai {creditInfo.credits} crediti</p>
                        <p className="text-sm text-emerald-600">Questo controllo consumerà 1 credito</p>
                      </div>
                    </>
                  ) : (
                    <>
                      <AlertCircle className="w-5 h-5 text-amber-600" />
                      <div>
                        <p className="font-medium text-amber-800">Crediti insufficienti</p>
                        <p className="text-sm text-amber-600">Acquista crediti per continuare</p>
                      </div>
                    </>
                  )}
                </div>
                {!creditInfo.can_check && (
                  <Button asChild className="swiss-btn-accent">
                    <Link to="/credits">Acquista Crediti</Link>
                  </Button>
                )}
              </div>
            </CardContent>
          </Card>
        )}
        
        {/* Header */}
        <div>
          <h1 className="text-2xl md:text-3xl font-bold tracking-tight">Nuova Busta Paga</h1>
          <p className="text-muted-foreground mt-1">
            Carica un PDF per estrazione automatica o inserisci i dati manualmente
          </p>
        </div>

        {error && (
          <div className="flex items-center gap-2 p-4 bg-red-50 border border-red-200 text-red-700 text-sm rounded-sm">
            <AlertCircle className="w-4 h-4 flex-shrink-0" />
            {error}
          </div>
        )}

        {/* PDF Upload Section */}
        <Card className="swiss-card border-dashed border-2 border-forest/20 bg-mint/30">
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <Sparkles className="w-5 h-5 text-forest" />
              Estrazione Automatica con AI
            </CardTitle>
            <CardDescription>
              Carica il PDF della busta paga e l'AI estrarrà automaticamente tutti i dati
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {/* File Input */}
              <div className="flex flex-col sm:flex-row gap-4">
                <div className="flex-1">
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept=".pdf"
                    onChange={handleFileSelect}
                    className="hidden"
                    data-testid="pdf-input"
                  />
                  
                  {selectedFile ? (
                    <div className="flex items-center gap-3 p-4 bg-white border border-forest/20 rounded-sm">
                      <FileText className="w-8 h-8 text-forest" />
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-sm truncate">{selectedFile.name}</p>
                        <p className="text-xs text-muted-foreground">
                          {(selectedFile.size / 1024).toFixed(1)} KB
                        </p>
                      </div>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={clearFile}
                        className="text-muted-foreground hover:text-red-600"
                      >
                        <X className="w-4 h-4" />
                      </Button>
                    </div>
                  ) : (
                    <button
                      type="button"
                      onClick={() => fileInputRef.current?.click()}
                      className="w-full p-8 border-2 border-dashed border-slate-300 rounded-sm hover:border-forest/50 hover:bg-mint/50 transition-colors text-center"
                      data-testid="upload-dropzone"
                    >
                      <Upload className="w-10 h-10 text-slate-400 mx-auto mb-3" />
                      <p className="font-medium text-sm">Clicca per selezionare un PDF</p>
                      <p className="text-xs text-muted-foreground mt-1">oppure trascina qui il file</p>
                    </button>
                  )}
                </div>
                
                <Button
                  type="button"
                  onClick={handleExtractPDF}
                  disabled={!selectedFile || extracting}
                  className="swiss-btn-accent h-auto py-4 px-6"
                  data-testid="extract-btn"
                >
                  {extracting ? (
                    <span className="flex items-center gap-2">
                      <span className="spinner" />
                      Estrazione AI...
                    </span>
                  ) : (
                    <>
                      <Sparkles className="w-4 h-4 mr-2" />
                      Estrai Dati
                    </>
                  )}
                </Button>
              </div>

              {/* Extraction Confidence */}
              {extractionConfidence && (
                <div className={`flex items-center gap-2 p-3 rounded-sm ${
                  extractionConfidence === 'high' ? 'bg-emerald-50 text-emerald-700' :
                  extractionConfidence === 'medium' ? 'bg-amber-50 text-amber-700' :
                  'bg-red-50 text-red-700'
                }`}>
                  <CheckCircle className="w-4 h-4" />
                  <span className="text-sm">
                    {extractionConfidence === 'high' && 'Estrazione completata con alta confidenza. Verifica i dati.'}
                    {extractionConfidence === 'medium' && 'Alcuni dati potrebbero richiedere verifica manuale.'}
                    {extractionConfidence === 'low' && 'Molti dati sono stati stimati. Verifica attentamente tutti i campi.'}
                  </span>
                </div>
              )}
              
              {/* Mostra selettore CCNL dopo l'estrazione */}
              {extractionConfidence && !showCcnlSelector && (
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setShowCcnlSelector(true)}
                  className="w-full border-dashed border-2 border-forest/30 hover:border-forest hover:bg-mint/30"
                  data-testid="show-ccnl-selector-btn"
                >
                  <Briefcase className="w-4 h-4 mr-2" />
                  Specifica CCNL e Livello manualmente
                </Button>
              )}
            </div>
          </CardContent>
        </Card>

        {/* CCNL e Livello Selector - Mostrato dopo estrazione */}
        {showCcnlSelector && (
          <Card className="swiss-card border-forest/20 bg-gradient-to-br from-mint/50 to-white">
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="text-lg flex items-center gap-2">
                    <Briefcase className="w-5 h-5 text-forest" />
                    Selezione CCNL e Livello
                  </CardTitle>
                  <CardDescription>
                    Seleziona manualmente il contratto e il livello per un confronto preciso con le tabelle retributive
                  </CardDescription>
                </div>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => {
                    setShowCcnlSelector(false);
                    setSelectedCcnl('');
                    setSelectedLivello('');
                  }}
                  className="text-muted-foreground hover:text-red-600"
                >
                  <X className="w-4 h-4" />
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {/* Selezione CCNL */}
                <div className="space-y-2">
                  <Label className="label-uppercase">CCNL Applicato</Label>
                  <Select 
                    value={selectedCcnl} 
                    onValueChange={(v) => {
                      setSelectedCcnl(v);
                      setSelectedLivello('');
                      setSimonaStage('ccnl_selected'); // Aggiorna Simona
                    }}
                  >
                    <SelectTrigger className="swiss-input" data-testid="select-ccnl">
                      <SelectValue placeholder="Seleziona il contratto" />
                    </SelectTrigger>
                    <SelectContent>
                      {CCNL_LIST.map(ccnl => (
                        <SelectItem key={ccnl.id} value={ccnl.id}>{ccnl.nome}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {/* Selezione Livello */}
                <div className="space-y-2">
                  <Label className="label-uppercase">Livello Inquadramento</Label>
                  <Select 
                    value={selectedLivello} 
                    onValueChange={(v) => {
                      setSelectedLivello(v);
                      setSimonaStage('ready_to_submit'); // Aggiorna Simona quando pronto
                    }}
                    disabled={!selectedCcnl || loadingLivelli}
                  >
                    <SelectTrigger className="swiss-input" data-testid="select-livello">
                      <SelectValue placeholder={loadingLivelli ? "Caricamento..." : "Seleziona il livello"} />
                    </SelectTrigger>
                    <SelectContent>
                      {livelliDisponibili.map(liv => (
                        <SelectItem key={liv.codice} value={liv.codice}>
                          {liv.codice} - {liv.nome} (min. €{liv.totale_minimo?.toFixed(2)})
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {/* Info box livello selezionato */}
              {selectedLivello && livelliDisponibili.length > 0 && (
                <div className="mt-4 p-4 bg-white rounded-lg border border-forest/20">
                  {(() => {
                    const livInfo = livelliDisponibili.find(l => l.codice === selectedLivello);
                    if (!livInfo) return null;
                    return (
                      <div className="space-y-2">
                        <div className="flex items-center gap-2">
                          <Info className="w-4 h-4 text-forest" />
                          <span className="font-medium text-forest">
                            Livello {livInfo.codice} - {livInfo.nome}
                          </span>
                        </div>
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
                          <div className="bg-slate-50 p-2 rounded">
                            <p className="text-muted-foreground text-xs">Paga Base</p>
                            <p className="font-mono font-medium">€{livInfo.paga_base?.toFixed(2)}</p>
                          </div>
                          <div className="bg-slate-50 p-2 rounded">
                            <p className="text-muted-foreground text-xs">Contingenza</p>
                            <p className="font-mono font-medium">€{livInfo.contingenza?.toFixed(2)}</p>
                          </div>
                          <div className="bg-slate-50 p-2 rounded">
                            <p className="text-muted-foreground text-xs">Totale Minimo</p>
                            <p className="font-mono font-bold text-forest">€{livInfo.totale_minimo?.toFixed(2)}</p>
                          </div>
                          <div className="bg-slate-50 p-2 rounded">
                            <p className="text-muted-foreground text-xs">Scatto Anzianità</p>
                            <p className="font-mono font-medium">€{livInfo.scatto_anzianita?.toFixed(2)}</p>
                          </div>
                        </div>
                        {livInfo.mansioni && livInfo.mansioni.length > 0 && (
                          <div className="mt-2">
                            <p className="text-xs text-muted-foreground">Mansioni tipiche:</p>
                            <p className="text-xs text-slate-600">{livInfo.mansioni.join(', ')}</p>
                          </div>
                        )}
                      </div>
                    );
                  })()}
                </div>
              )}
            </CardContent>
          </Card>
        )}

        <div className="relative">
          <Separator />
          <span className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 bg-white px-4 text-sm text-muted-foreground">
            oppure compila manualmente
          </span>
        </div>

        <form onSubmit={handleSubmit}>
          {/* Anagrafica */}
          <Card className="swiss-card mb-6">
            <CardHeader>
              <CardTitle className="text-lg">Dati Anagrafici</CardTitle>
              <CardDescription>Informazioni del dipendente e periodo di riferimento</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="form-grid">
                <div className="space-y-2">
                  <Label className="label-uppercase">Nome Dipendente *</Label>
                  <Input
                    name="employee_name"
                    value={formData.employee_name}
                    onChange={handleChange}
                    placeholder="Mario Rossi"
                    className="swiss-input"
                    data-testid="input-employee-name"
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label className="label-uppercase">Codice Fiscale *</Label>
                  <Input
                    name="fiscal_code"
                    value={formData.fiscal_code}
                    onChange={handleChange}
                    placeholder="RSSMRA80A01H501X"
                    className="swiss-input uppercase"
                    maxLength={16}
                    data-testid="input-fiscal-code"
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label className="label-uppercase">Azienda *</Label>
                  <Input
                    name="company_name"
                    value={formData.company_name}
                    onChange={handleChange}
                    placeholder="Nome Azienda S.r.l."
                    className="swiss-input"
                    data-testid="input-company-name"
                    required
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label className="label-uppercase">Mese *</Label>
                    <Select 
                      value={formData.period_month} 
                      onValueChange={(v) => handleSelectChange('period_month', v)}
                    >
                      <SelectTrigger className="swiss-input" data-testid="select-month">
                        <SelectValue placeholder="Seleziona" />
                      </SelectTrigger>
                      <SelectContent>
                        {MONTHS.map(m => (
                          <SelectItem key={m.value} value={String(m.value)}>{m.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label className="label-uppercase">Anno *</Label>
                    <Select 
                      value={formData.period_year} 
                      onValueChange={(v) => handleSelectChange('period_year', v)}
                    >
                      <SelectTrigger className="swiss-input" data-testid="select-year">
                        <SelectValue placeholder="Seleziona" />
                      </SelectTrigger>
                      <SelectContent>
                        {YEARS.map(y => (
                          <SelectItem key={y} value={String(y)}>{y}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Retribuzione */}
          <Card className="swiss-card mb-6">
            <CardHeader>
              <CardTitle className="text-lg">Retribuzione Lorda</CardTitle>
              <CardDescription>Componenti della retribuzione mensile</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="form-grid">
                <div className="space-y-2">
                  <Label className="label-uppercase">Retribuzione Lorda Totale *</Label>
                  <div className="relative">
                    <Input
                      name="gross_salary"
                      type="number"
                      step="0.01"
                      value={formData.gross_salary}
                      onChange={handleChange}
                      placeholder="2500.00"
                      className="swiss-input pr-8 font-mono"
                      data-testid="input-gross-salary"
                      required
                    />
                    <span className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground">€</span>
                  </div>
                </div>
                <div className="space-y-2">
                  <Label className="label-uppercase">Paga Base *</Label>
                  <div className="relative">
                    <Input
                      name="base_salary"
                      type="number"
                      step="0.01"
                      value={formData.base_salary}
                      onChange={handleChange}
                      placeholder="1800.00"
                      className="swiss-input pr-8 font-mono"
                      data-testid="input-base-salary"
                      required
                    />
                    <span className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground">€</span>
                  </div>
                </div>
                <div className="space-y-2">
                  <Label className="label-uppercase">Contingenza</Label>
                  <div className="relative">
                    <Input
                      name="contingency"
                      type="number"
                      step="0.01"
                      value={formData.contingency}
                      onChange={handleChange}
                      placeholder="500.00"
                      className="swiss-input pr-8 font-mono"
                      data-testid="input-contingency"
                    />
                    <span className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground">€</span>
                  </div>
                </div>
                <div className="space-y-2">
                  <Label className="label-uppercase">Terzo Elemento</Label>
                  <div className="relative">
                    <Input
                      name="third_element"
                      type="number"
                      step="0.01"
                      value={formData.third_element}
                      onChange={handleChange}
                      placeholder="100.00"
                      className="swiss-input pr-8 font-mono"
                      data-testid="input-third-element"
                    />
                    <span className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground">€</span>
                  </div>
                </div>
                <div className="space-y-2">
                  <Label className="label-uppercase">Ore Straordinario</Label>
                  <Input
                    name="overtime_hours"
                    type="number"
                    step="0.5"
                    value={formData.overtime_hours}
                    onChange={handleChange}
                    placeholder="0"
                    className="swiss-input font-mono"
                    data-testid="input-overtime-hours"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="label-uppercase">Importo Straordinario</Label>
                  <div className="relative">
                    <Input
                      name="overtime_amount"
                      type="number"
                      step="0.01"
                      value={formData.overtime_amount}
                      onChange={handleChange}
                      placeholder="0.00"
                      className="swiss-input pr-8 font-mono"
                      data-testid="input-overtime-amount"
                    />
                    <span className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground">€</span>
                  </div>
                </div>
                <div className="space-y-2 col-span-2 sm:col-span-1">
                  <Label className="label-uppercase">Premi e Bonus</Label>
                  <div className="relative">
                    <Input
                      name="bonuses"
                      type="number"
                      step="0.01"
                      value={formData.bonuses}
                      onChange={handleChange}
                      placeholder="0.00"
                      className="swiss-input pr-8 font-mono"
                      data-testid="input-bonuses"
                    />
                    <span className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground">€</span>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Trattenute */}
          <Card className="swiss-card mb-6">
            <CardHeader>
              <CardTitle className="text-lg">Trattenute</CardTitle>
              <CardDescription>Contributi e imposte trattenute</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="form-grid">
                <div className="space-y-2">
                  <Label className="label-uppercase">INPS Dipendente (9.19%) *</Label>
                  <div className="relative">
                    <Input
                      name="inps_employee"
                      type="number"
                      step="0.01"
                      value={formData.inps_employee}
                      onChange={handleChange}
                      placeholder="229.75"
                      className="swiss-input pr-8 font-mono"
                      data-testid="input-inps"
                      required
                    />
                    <span className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground">€</span>
                  </div>
                </div>
                <div className="space-y-2">
                  <Label className="label-uppercase">IRPEF *</Label>
                  <div className="relative">
                    <Input
                      name="irpef"
                      type="number"
                      step="0.01"
                      value={formData.irpef}
                      onChange={handleChange}
                      placeholder="450.00"
                      className="swiss-input pr-8 font-mono"
                      data-testid="input-irpef"
                      required
                    />
                    <span className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground">€</span>
                  </div>
                </div>
                <div className="space-y-2">
                  <Label className="label-uppercase">Addizionale Regionale</Label>
                  <div className="relative">
                    <Input
                      name="regional_tax"
                      type="number"
                      step="0.01"
                      value={formData.regional_tax}
                      onChange={handleChange}
                      placeholder="30.00"
                      className="swiss-input pr-8 font-mono"
                      data-testid="input-regional-tax"
                    />
                    <span className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground">€</span>
                  </div>
                </div>
                <div className="space-y-2">
                  <Label className="label-uppercase">Addizionale Comunale</Label>
                  <div className="relative">
                    <Input
                      name="municipal_tax"
                      type="number"
                      step="0.01"
                      value={formData.municipal_tax}
                      onChange={handleChange}
                      placeholder="15.00"
                      className="swiss-input pr-8 font-mono"
                      data-testid="input-municipal-tax"
                    />
                    <span className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground">€</span>
                  </div>
                </div>
                <div className="space-y-2 col-span-2 sm:col-span-1">
                  <Label className="label-uppercase">Altre Trattenute</Label>
                  <div className="relative">
                    <Input
                      name="other_deductions"
                      type="number"
                      step="0.01"
                      value={formData.other_deductions}
                      onChange={handleChange}
                      placeholder="0.00"
                      className="swiss-input pr-8 font-mono"
                      data-testid="input-other-deductions"
                    />
                    <span className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground">€</span>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Netto e TFR */}
          <Card className="swiss-card mb-6">
            <CardHeader>
              <CardTitle className="text-lg">Netto e TFR</CardTitle>
              <CardDescription>Importo netto e accantonamento TFR</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="form-grid">
                <div className="space-y-2">
                  <Label className="label-uppercase">Netto in Busta *</Label>
                  <div className="relative">
                    <Input
                      name="net_salary"
                      type="number"
                      step="0.01"
                      value={formData.net_salary}
                      onChange={handleChange}
                      placeholder="1775.25"
                      className="swiss-input pr-8 font-mono text-lg"
                      data-testid="input-net-salary"
                      required
                    />
                    <span className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground">€</span>
                  </div>
                </div>
                <div className="space-y-2">
                  <Label className="label-uppercase">TFR Maturato (6.91%)</Label>
                  <div className="relative">
                    <Input
                      name="tfr_accrued"
                      type="number"
                      step="0.01"
                      value={formData.tfr_accrued}
                      onChange={handleChange}
                      placeholder="172.75"
                      className="swiss-input pr-8 font-mono"
                      data-testid="input-tfr"
                    />
                    <span className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground">€</span>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          <Separator className="my-8" />

          {/* Submit */}
          <div className="flex flex-col sm:flex-row gap-4 justify-end">
            <Button
              type="button"
              variant="outline"
              className="swiss-btn-secondary"
              onClick={() => navigate('/dashboard')}
            >
              Annulla
            </Button>
            <Button
              type="submit"
              className="swiss-btn-primary"
              disabled={loading}
              data-testid="submit-payslip"
            >
              {loading ? (
                <span className="flex items-center gap-2">
                  <span className="spinner" />
                  Verifica in corso...
                </span>
              ) : (
                <>
                  <Calculator className="w-4 h-4 mr-2" />
                  Verifica Busta Paga
                  <ArrowRight className="w-4 h-4 ml-2" />
                </>
              )}
            </Button>
          </div>
        </form>
      </div>
      
      {/* Simona - Assistente Virtuale (solo su web, non su iOS/Android) */}
      {!isNativeApp && (
        <SimonaAssistant
          stage={simonaStage}
          isMinimized={simonaMinimized}
          onToggleMinimize={() => setSimonaMinimized(!simonaMinimized)}
        />
      )}
    </DashboardLayout>
  );
}
